import CloseIcon from "~icons/fa6-solid/x";
import NewTabIcon from "~icons/local/arrow-up-right-from-square";

import { components } from "@/lib/ApiSchema";
import Button from "@/lib/Button";
import List, { Column } from "@/lib/List";
import $api from "@/lib/queries/openapi";
import Search from "@/lib/Search";
import { getUrl } from "@/lib/useToPage";
import dayjs from "dayjs";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { datetimeFormat } from "../rfq/timeline/utils";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

export default function PriorPartEstimatesModal({
  initialSearch,
  currentPartId,
  close,
}: {
  initialSearch: string;
  currentPartId: string;
  close: () => void;
}) {
  const [search, setSearch] = useState(initialSearch);
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();

  const { mutate: copyPartOperations, isPending: isCopying } = $api.useMutation(
    "post",
    "/api/teams/{team_slug}/requested_parts/{id}/copy_operations_from/{from_id}",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            "get",
            "/api/teams/{team_slug}/requested_parts/{id}",
            {
              params: {
                path: {
                  team_slug: teamSlug!,
                  id: currentPartId,
                },
              },
            },
          ],
        });

        toast.success("Successfully copied part operations.");
        close();
      },
    },
  );

  const columns: Column<components["schemas"]["RequestedPartSearchSchema"]>[] =
    [
      { label: "Part Number", content: ({ part_number }) => part_number },
      {
        label: "Part Revision",
        content: ({ revision_number }) => revision_number,
      },
      {
        label: "Date",
        content: ({ created_at }) => dayjs(created_at).format(datetimeFormat),
      },
      {
        label: "Quantities",
        content: ({ requested_quantities }) => requested_quantities.join(", "),
      },
      {
        label: "",
        content: ({ id }) => (
          <Button
            primary
            loading={isCopying}
            onClick={() => {
              copyPartOperations({
                params: {
                  path: {
                    team_slug: teamSlug!,
                    id: currentPartId,
                    from_id: id,
                  },
                },
              });
            }}
          >
            Import
          </Button>
        ),
      },
      {
        label: "",
        content: ({ id, rfq_id }) => (
          <Link
            to={getUrl("part", {
              teamSlug: teamSlug!,
              rfqId: rfq_id,
              partId: id,
            })}
            target="_blank"
          >
            <Button>
              <div className="flex items-center gap-2">
                <NewTabIcon className="size-3" />
                <span>Open in New Tab</span>
              </div>
            </Button>
          </Link>
        ),
      },
    ];

  const { data, isPending } = $api.useQuery(
    "get",
    "/api/teams/{team_slug}/requested_parts/find_with_operations",
    {
      params: {
        path: {
          team_slug: useParams().teamSlug!,
        },
        query: {
          search,
        },
      },
    },
  );

  return (
    <div className="w-240 h-120 flex flex-col gap-2 p-4 bg-white">
      <div className="flex items-center gap-2 justify-between">
        <div className="font-bold text-lg">Recent Estimates</div>
        <Search
          className="flex-1 w-52"
          value={search}
          onChange={(value) => setSearch(value)}
          placeholder="Search by part number"
          autoFocus
        />
        <CloseIcon
          onClick={close}
          className="cursor-pointer text-slate-400 hover:text-slate-600 size-3"
        />
      </div>
      <List<components["schemas"]["RequestedPartSearchSchema"]>
        columns={columns}
        list={data?.items.filter((p) => p.id !== currentPartId) || []}
        isLoading={isPending}
        noBorder
        emptyState={
          <div className="text-center text-gray-500 py-8">
            No recent estimates found.
          </div>
        }
      />
    </div>
  );
}
